import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import FastPhotoEditor from "../editor/FastPhotoEditor";
import { PoopRecord } from "../../types/poop";
import { ReviewRecord, SampleExport, dataSamplesApi } from "../../services/api.dataSamples";

/**
 * Reviews one export's photos in the AI Review editor. Saves go to app.poop —
 * the row AI Review edits — so a photo checked here leaves the AI Review
 * queue and, approved, is ready to train; one checked in AI Review shows up
 * here as checked. Downloads ship the reviewed labels and leave out photos
 * marked not good for ML.
 */

interface Props {
  exp: SampleExport;
  buyerName: string;
  onClose: () => void;
  /** Saves landed: refresh the counts. */
  onChanged: () => void;
}

/** NOT NOW = checked and kept out of training: a person saved it without a decision. */
const isDeferred = (r: ReviewRecord) =>
  (r.image_good_for_ml === null || r.image_good_for_ml === undefined) && r.reviewed_via !== null;
const isChecked = (r: ReviewRecord) =>
  (r.image_good_for_ml !== null && r.image_good_for_ml !== undefined) || isDeferred(r);

const SampleReview: React.FC<Props> = ({ exp, buyerName, onClose, onChanged }) => {
  const [records, setRecords] = useState<ReviewRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<{ records: ReviewRecord[]; start: number } | null>(null);

  const load = useCallback(() => {
    setRecords(null);
    setError(null);
    dataSamplesApi
      .reviewQueue(exp.id)
      .then(({ records: list }) => setRecords(list))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [exp.id]);

  useEffect(load, [load]);

  const counts = useMemo(() => {
    const list = records ?? [];
    return {
      total: list.length,
      approved: list.filter((r) => r.image_good_for_ml === true).length,
      rejected: list.filter((r) => r.image_good_for_ml === false).length,
      deferred: list.filter(isDeferred).length,
      inAiReview: list.filter((r) => isChecked(r) && r.reviewed_via === "ai_review").length,
      deleted: list.filter((r) => r.deleted_in_app).length,
    };
  }, [records]);
  const unchecked = counts.total - counts.approved - counts.rejected - counts.deferred;

  const saveRecord = useCallback(
    (record: PoopRecord, data: Partial<PoopRecord>) =>
      dataSamplesApi.saveReview(exp.id, (record as ReviewRecord).seq, data).catch(() => null),
    [exp.id],
  );

  const renderBadges = useCallback((record: PoopRecord) => {
    const r = record as ReviewRecord;
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Chip size="small" label={r.sample_id} sx={{ bgcolor: "rgba(0,0,0,0.65)", color: "#fff" }} />
        {isChecked(r) && r.reviewed_via === "ai_review" && (
          <Chip size="small" color="success" label="Checked in AI Review" />
        )}
        {r.deleted_in_app && <Chip size="small" color="warning" label="User deleted this scan in the app" />}
      </Stack>
    );
  }, []);

  const begin = (onlyUnchecked: boolean) => {
    if (!records) return;
    const list = onlyUnchecked ? records.filter((r) => !isChecked(r)) : records;
    const firstOpen = list.findIndex((r) => !isChecked(r));
    setSession({ records: list, start: Math.max(0, firstOpen) });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "background.default",
        zIndex: 1250,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 3, width: "100%", maxWidth: 520, position: "relative" }}>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>
          Review · {buyerName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The AI Review editor, on this export's photos. Yes: ships with your labels and joins Ready to Train. NOT NOW:
          ships with your labels, stays out of Ready to Train. No: left out of every download. Checked photos leave the
          AI Review queue.
        </Typography>

        {error ? (
          <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
            {error}
          </Alert>
        ) : !records ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Matching these photos with AI Review. The first time can take a few minutes.
            </Typography>
          </Stack>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1, mb: 2 }}>
              <Chip label={`${unchecked.toLocaleString()} to check`} color={unchecked ? "primary" : "default"} />
              <Chip label={`${counts.approved.toLocaleString()} approved`} color="success" variant="outlined" />
              {counts.deferred > 0 && (
                <Chip label={`${counts.deferred.toLocaleString()} kept out of training`} variant="outlined" />
              )}
              <Chip label={`${counts.rejected.toLocaleString()} not good for ML`} color="error" variant="outlined" />
            </Stack>
            {counts.inAiReview > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {counts.inAiReview.toLocaleString()} of these were already checked in AI Review.
              </Typography>
            )}
            {counts.deleted > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {counts.deleted.toLocaleString()} photo{counts.deleted === 1 ? " was" : "s were"} later deleted by the
                user in the app. Consider marking {counts.deleted === 1 ? "it" : "them"} not good for ML so{" "}
                {counts.deleted === 1 ? "it isn't" : "they aren't"} delivered.
              </Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button fullWidth variant="contained" size="large" disabled={unchecked === 0} onClick={() => begin(true)}>
                Check the {unchecked.toLocaleString()} left
              </Button>
              <Button fullWidth variant="outlined" size="large" onClick={() => begin(false)}>
                Go through all {counts.total.toLocaleString()}
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      {session && (
        <FastPhotoEditor
          open
          records={session.records}
          initialIndex={session.start}
          saveRecord={saveRecord}
          allowCrop={false}
          renderBadges={renderBadges}
          onClose={() => setSession(null)}
          onUpdate={() => {
            onChanged();
            load();
          }}
        />
      )}
    </Box>
  );
};

export default SampleReview;
